import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import {
  OpenInNewTabButton,
  ReferenceContent,
  ReferenceItem,
  ReferenceTitle,
} from 'features/chatbot/components/conversationMessage/styles'
import { useInstrumentReferenceFallback } from 'features/chatbot/hooks/useInstrumentReferenceFallback'
import { Source } from 'features/chatbot/types/conversation'
import {
  createDocumentTab,
  useKnowledgeDocumentUXContextOptional,
} from 'features/knowledgeDocumentViewer'
import { FileText, LineChart, Plus } from 'lucide-react'
import React, { useCallback, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'

const DOCUMENT_ID_KEYS = ['id', 'document_id', 'documentId'] as const

function isInstrumentSource(source: Source): boolean {
  return source.id.startsWith('inst-')
}

function resolveDocumentId(source: Source): string {
  for (const key of DOCUMENT_ID_KEYS) {
    const val = source.data[key]
    if (typeof val === 'string' && val) return val
  }
  return source.id
}

function formatInstrumentGroupLabel(instruments: Source[]): string {
  if (instruments.length === 1) return instruments[0].text
  if (instruments.length === 2) return instruments.map((i) => i.text).join(', ')
  const first = instruments[0].text
  const rest = instruments.length - 1
  return t`${first} +${rest} more`
}

/** Collects all entry IDs and widens to the broadest time window across instrument sources. */
function collectInstrumentEntryIds(instruments: Source[]): {
  entryIds: string[]
  startDatetime?: string
  endDatetime?: string
} {
  const entryIds: string[] = []
  let startDatetime: string | undefined
  let endDatetime: string | undefined

  for (const inst of instruments) {
    entryIds.push(inst.data.entryId as string)
    const start = inst.data.startDatetime as string | undefined
    const end = inst.data.endDatetime as string | undefined
    if (start && (!startDatetime || start < startDatetime))
      startDatetime = start
    if (end && (!endDatetime || end > endDatetime)) endDatetime = end
  }

  return { entryIds, startDatetime, endDatetime }
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.25rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin-top: 0.25rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-size: 0.6875rem;

  svg {
    width: 0.625rem;
    height: 0.625rem;
    flex-shrink: 0;
  }
`

const AnimatedReferenceItem = styled(ReferenceItem)`
  animation: ${fadeIn} 0.25s ease-out;
`

interface StreamingReferencesProps {
  sources: Source[]
}

export const StreamingReferences: React.FC<StreamingReferencesProps> = ({
  sources,
}) => {
  useLingui()
  const documentUXContext = useKnowledgeDocumentUXContextOptional()

  const { documents, instruments } = useMemo(() => {
    const docs: Source[] = []
    const insts: Source[] = []
    const seenNames = new Set<string>()
    for (const source of sources) {
      if (seenNames.has(source.text)) continue
      seenNames.add(source.text)
      if (isInstrumentSource(source)) {
        insts.push(source)
      } else {
        docs.push(source)
      }
    }
    return { documents: docs, instruments: insts }
  }, [sources])

  const handleOpenDocument = useCallback(
    (source: Source) => {
      if (!documentUXContext) return
      const newTab = createDocumentTab(resolveDocumentId(source))
      documentUXContext.addTab(newTab)
    },
    [documentUXContext]
  )

  const openInstrumentsFallback = useInstrumentReferenceFallback()

  const handleOpenInstrumentGroup = useCallback(() => {
    if (instruments.length === 0) return

    const { entryIds, startDatetime, endDatetime } =
      collectInstrumentEntryIds(instruments)
    const timeWindow =
      startDatetime && endDatetime
        ? { from: startDatetime, to: endDatetime }
        : undefined
    openInstrumentsFallback(entryIds, timeWindow)
  }, [instruments, openInstrumentsFallback])

  const totalCount = documents.length + (instruments.length > 0 ? 1 : 0)
  if (totalCount === 0) return null

  return (
    <Container>
      <Header>
        <FileText />
        {t`References (${totalCount})`}
      </Header>
      {documents.map((source) => (
        <AnimatedReferenceItem key={`ref-${source.id}`}>
          <ReferenceContent onClick={() => handleOpenDocument(source)}>
            <FileText />
            <ReferenceTitle>{source.text}</ReferenceTitle>
          </ReferenceContent>
          <OpenInNewTabButton onClick={() => handleOpenDocument(source)}>
            <Plus />
          </OpenInNewTabButton>
        </AnimatedReferenceItem>
      ))}
      {instruments.length > 0 && (
        <AnimatedReferenceItem>
          <ReferenceContent onClick={handleOpenInstrumentGroup}>
            <LineChart />
            <ReferenceTitle>
              {formatInstrumentGroupLabel(instruments)}
            </ReferenceTitle>
          </ReferenceContent>
          <OpenInNewTabButton onClick={handleOpenInstrumentGroup}>
            <Plus />
          </OpenInNewTabButton>
        </AnimatedReferenceItem>
      )}
    </Container>
  )
}
